import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import {
  getRegistrationEmail,
  hasRegistrationEmail,
  setVerificationToken,
} from '../../utils/registrationSession';
import OtpVerificationForm, { setOtpResendCooldown } from '../shared/OtpVerificationForm';
import RegisterLayout from './RegisterLayout';

const COOLDOWN_SECONDS = 60;

export default function RegisterOtpStep() {
  const navigate = useNavigate();
  const email = getRegistrationEmail();
  const [otp, setOtp] = useState('');
  const [serverError, setServerError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [resending, setResending] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(COOLDOWN_SECONDS);

  useEffect(() => {
    if (!hasRegistrationEmail()) {
      navigate('/register', { replace: true });
    }
  }, [navigate]);

  const onVerify = async (e) => {
    e.preventDefault();
    setServerError('');

    if (otp.length !== 6) {
      setServerError('Enter the 6-digit verification code');
      return;
    }

    setSubmitting(true);
    try {
      const { data } = await api.post('auth/register/verify-otp', { email, otp });
      const token = data?.data?.verification_token;
      if (!token) {
        throw new Error('Verification failed');
      }
      setVerificationToken(token);
      navigate('/register/school');
    } catch (err) {
      const msg = err?.response?.data?.message || err?.message || 'Verification failed';
      setServerError(msg);
      setOtp('');
    } finally {
      setSubmitting(false);
    }
  };

  const onResend = async () => {
    setServerError('');
    setResending(true);
    try {
      await api.post('auth/register/resend-otp', { email });
      setOtp('');
      setResendCooldown(COOLDOWN_SECONDS);
    } catch (err) {
      const msg = err?.response?.data?.message || err?.message || 'Could not resend code';
      const retryAfter = err?.response?.data?.data?.retry_after;
      setOtpResendCooldown(setResendCooldown, retryAfter, COOLDOWN_SECONDS);
      setServerError(msg);
    } finally {
      setResending(false);
    }
  };

  return (
    <RegisterLayout
      step={2}
      title="Verify your email"
      description={
        <>
          We&apos;ve sent a verification code to <strong className="text-foreground">{email}</strong>
        </>
      }
    >
      <OtpVerificationForm
        otp={otp}
        onOtpChange={setOtp}
        onSubmit={onVerify}
        onResend={onResend}
        onCancel={() => navigate('/register')}
        submitting={submitting}
        resending={resending}
        serverError={serverError}
        cooldownSeconds={resendCooldown}
        submitLabel="Verify & Continue"
        submittingLabel="Verifying…"
        cancelLabel="Back"
      />
    </RegisterLayout>
  );
}
